/**
 * Feature flag — Scheduling AI command bar (natural-language scheduling
 * demo-proof; PROD-1316 intent-service track).
 *
 * Reads `VITE_SCHEDULING_AI`. Vite inlines the value at build time, so
 * this resolves on first call and does not subscribe to runtime changes —
 * toggling the flag requires a rebuild (dev server restart).
 *
 * Default: OFF (fail-closed). When the flag is absent, empty, or anything
 * other than 'true'/'1'/'on', the entire Scheduling-AI surface — command
 * bar, ghost draft rows, alternatives fetch — renders NOTHING and fires
 * NO network calls. Gated consumers: `frame-scheduling.tsx`.
 *
 * Mirrors the consumption pattern of `useClinicEditRailEnabled`
 * (`src/lib/use-clinic-edit-rail-enabled.ts`) and reuses its `coerceFlag`
 * so the string→bool contract stays single-sourced.
 */

import { coerceFlag } from './coerce-flag';

/**
 * Returns whether the Scheduling-AI command-bar surface is enabled for
 * the current build. Read once from `import.meta.env.VITE_SCHEDULING_AI`.
 *
 * @returns `true` only when the flag string coerces to truthy; `false`
 *          for absent/empty/false — fail-closed.
 */
export function useSchedulingAiEnabled(): boolean {
  return schedulingAiEnabled();
}

/**
 * The same flag outside a component (module-level names such as the rail's
 * `dashboard` label, which must follow the inbox gate exactly).
 */
export function schedulingAiEnabled(): boolean {
  return coerceFlag(import.meta.env.VITE_SCHEDULING_AI);
}
